import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick, omit } from 'lodash/fp';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    CreateBankDocument,
    type CreateBankMutation,
    type CreateBankMutationVariables,
} from '../../../api/mutations/createBank';
import {
    UpdateBankAvailableFinanceProductTypesDocument,
    type UpdateBankAvailableFinanceProductTypesMutation,
    type UpdateBankAvailableFinanceProductTypesMutationVariables,
} from '../../../api/mutations/updateBankAvailableFinanceProductTypes';
import { BankIntegrationProvider, type BankIntegrationSettings } from '../../../api/types';
import useLoadingButton from '../../../components/button/useLoadingButton';
import Form from '../../../components/fields/Form';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../utilities/useHandleError';
import { useBankFormValidator, type NewBankFormValues } from '../BankDetailsPage/BankForm';
import CreateBankForm, { defaultValues } from '../BankDetailsPage/BankForm/CreateBankFrom';
import useHandleEnbdAssets from '../BankDetailsPage/BankForm/useHandleEnbdAssets';
import useHandleBankAssets from '../BankDetailsPage/useHandleBankAssets';

export const getBankIntegrationSettings = ({
    integrationProvider,
    integrationSettings,
}: {
    integrationProvider: BankIntegrationProvider;
    integrationSettings: BankIntegrationSettings;
}) => {
    switch (integrationProvider) {
        case BankIntegrationProvider.Email:
            return pick('email', integrationSettings);

        case BankIntegrationProvider.Hlf:
            return pick('hlf', integrationSettings);

        case BankIntegrationProvider.Hlfv2:
            return pick('hlfV2', integrationSettings);

        case BankIntegrationProvider.Uob:
            return pick('uob', integrationSettings);

        case BankIntegrationProvider.Dbs:
            return pick('dbs', integrationSettings);

        case BankIntegrationProvider.BmwFs:
            return pick('bmwFs', integrationSettings);

        case BankIntegrationProvider.Maybank:
            return omit(['maybank.generatedId', 'maybank.generatedSecrets'], pick('maybank', integrationSettings));

        case BankIntegrationProvider.Enbd:
            return omit(['enbd.stampRight', 'enbd.stampBottom'], pick('enbd', integrationSettings));

        default:
            return null;
    }
};

const AddBankPage = () => {
    const { t } = useTranslation(['bankDetails']);
    const navigate = useNavigate();
    const { setLoading, LoadingButton } = useLoadingButton();
    const validate = useBankFormValidator();

    const apolloClient = useApolloClient();

    const handleBankAssets = useHandleBankAssets();
    const handleEnbdBankAssets = useHandleEnbdAssets();

    const onSubmit = useHandleError<NewBankFormValues>(
        async values => {
            const {
                logo,
                font,
                integrationProvider,
                integrationSettings,
                availableFinanceProductTypes,
                showMarginOfFinance,
                ...settings
            } = values;

            // submitting message
            message.loading({
                content: t('bankDetails:messages.createSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            const { data } = await apolloClient.mutate<CreateBankMutation, CreateBankMutationVariables>({
                mutation: CreateBankDocument,
                variables: {
                    settings,
                    integrationSettings: getBankIntegrationSettings({ integrationProvider, integrationSettings }),
                },
            });

            try {
                // handle bank asset upload
                // if upload fails, we still redirect to edit page to avoid duplicate create
                await handleBankAssets(data.bank.id, values);

                // If integration is enbd
                if (integrationProvider === BankIntegrationProvider.Enbd) {
                    await handleEnbdBankAssets(data.bank.id, values.integrationSettings.enbd);
                }
            } catch (error) {
                console.warn(error);
            }

            await apolloClient
                .mutate<
                    UpdateBankAvailableFinanceProductTypesMutation,
                    UpdateBankAvailableFinanceProductTypesMutationVariables
                >({
                    mutation: UpdateBankAvailableFinanceProductTypesDocument,
                    variables: {
                        bankId: data.bank.id,
                        settings: { availableFinanceProductTypes, showMarginOfFinance },
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('bankDetails:messages.createSuccessful'),
                key: 'primary',
            });
            setLoading(false);
            // then go back to list
            navigate(`/admin/banks`);
        },
        [apolloClient, handleBankAssets, handleEnbdBankAssets, navigate, setLoading, t]
    );

    return (
        <ConsolePageWithHeader
            footer={[
                <LoadingButton key="submit" form="bankForm" htmlType="submit" type="primary">
                    {t('bankDetails:actions.create')}
                </LoadingButton>,
            ]}
            onBack={() => navigate('/admin/banks')}
            title={t('bankDetails:addTitle')}
        >
            <Formik initialValues={defaultValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit, values }) => (
                    <Form id="bankForm" name="bankForm" onSubmitCapture={handleSubmit}>
                        <CreateBankForm />
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default AddBankPage;
